<template>
  <div :class="containerClass" @contextmenu="onContextMenu">
    <RouterLink :to="{ name: route }" :class="linkClass">
      <span aria-hidden="true" class="absolute inset-0" />

      <div v-if="showIcon" :class="iconClass">
        <component :is="icon" class="h-6 w-6" />
      </div>

      <span v-if="showTitle" :class="titleClass">
        {{ id ? t('widgets.' + id + '.title') : title }}
      </span>
    </RouterLink>

    <div v-if="showText" class="shortcut-text mt-2 text-sm leading-relaxed">
      <span>
      <slot>{{ id ? t('widgets.' + id + '.text') : text }}</slot>
        </span>
    </div>

    <ContextMenu ref="menu" :model="items" v-if="isEditing">
      <template #itemicon="itm">
        <component
          :is="itm.item.icon"
          :class="[itm.class]" />
      </template>
    </ContextMenu>
  </div>
</template>

<script lang="ts" setup>
  import { computed, defineComponent } from 'vue'
  import { useI18n } from 'vue-i18n'
  import ContextMenu from 'primevue/contextmenu'
  import { useDashboard } from '@/composables/useDashboard.ts'
  import { FullWidgetIcon, ImageTextIcon, ShapeIcon, TitleIcon, TypeIcon } from '@/config/icons.ts'
  import { useShortcutWidgetBus } from '@/composables/useDashboardWidgetBuses.ts'

  type ShortcutType = 'full' | 'labelicon' | 'label' | 'icon'

  interface Props {
    icon?: ReturnType<typeof defineComponent>
    route: string
    text?: string
    title?: string
    type?: ShortcutType
    id?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    type: 'full'
  })

  const widgetType = ref<ShortcutType>(props.type)

  const isEditing = useDashboard().isEditing
  const { t } = useI18n()

  const shortcutBus = useShortcutWidgetBus()

  const menu = ref()
  const items = ref([
    {
      label: t('type'),
      icon: TypeIcon,
      items: [
        { label: t('icon'), icon: ShapeIcon, command: () => emitWidgetTypeChange('icon') },
        { label: t('title'), icon: TitleIcon, command: () => emitWidgetTypeChange('label') },
        { label: t('iconTitle'), icon: ImageTextIcon, command: () => emitWidgetTypeChange('labelicon') },
        { label: t('full'), icon: FullWidgetIcon, command: () => emitWidgetTypeChange('full') }
      ]
    }
  ])

  const onContextMenu = (event: MouseEvent) => {
    if (isEditing.value) {
      event.preventDefault()
      menu.value?.show(event)
    }
  }

  const emitWidgetTypeChange = async (widgetNewType: ShortcutType) => {
    if (!props.id)
      return

    widgetType.value = widgetNewType
    shortcutBus.emit({ type: widgetNewType, id: props.id })
  }

  const showIcon = computed(() => ['full', 'labelicon', 'icon'].includes(widgetType.value))
  const showTitle = computed(() => ['full', 'labelicon', 'label'].includes(widgetType.value))
  const showText = computed(() => widgetType.value === 'full')

  const containerClass = computed(() => {
    switch (widgetType.value) {
      case 'full':
        return 'group titrop-widget-shortcut titrop-widget-col-4 @container flex w-full min-w-0 flex-col overflow-hidden min-h-[80px]'
      case 'labelicon':
        return 'group titrop-widget-shortcut titrop-widget-col-3 @container flex w-full min-w-0 items-center overflow-hidden min-h-[40px]'
      case 'label':
        return 'group titrop-widget-shortcut titrop-widget-col-2 @container flex w-full min-w-0 items-center justify-center overflow-hidden min-h-[40px]'
      case 'icon':
        return 'group titrop-widget-shortcut titrop-widget-col-1 @container flex w-full min-w-0 items-center justify-center overflow-hidden min-h-[40px]'
      default:
        return 'group titrop-widget-shortcut titrop-widget-col-4 @container flex w-full min-w-0 flex-col overflow-hidden min-h-[80px]'
    }
  })

  const linkClass = computed(() => {
    switch (widgetType.value) {
      case 'full':
        return 'relative flex w-full min-w-0 flex-col items-center focus:outline-none text-current'
      case 'labelicon':
        return 'relative flex w-full min-w-0 flex-row items-center justify-center focus:outline-none text-current'
      case 'label':
        return 'relative flex w-full min-w-0 items-center justify-center focus:outline-none text-current'
      case 'icon':
        return 'relative flex w-full min-w-0 items-center justify-center focus:outline-none text-current'
      default:
        return 'relative flex w-full min-w-0 flex-col items-center focus:outline-none text-current'
    }
  })

  const iconClass = computed(() =>
    'inline-flex shrink-0 rounded-lg p-2 bg-teal-50 text-teal-600 ring-2 ring-white ' +
    'dark:bg-teal-900 dark:ring-slate-900 group-hover:text-teal-700 group-hover:dark:text-teal-500'
  )

  const titleClass = computed(() => {
    switch (widgetType.value) {
      case 'full':
        return 'w-full min-w-0 truncate text-center font-bold'
      case 'label':
        return 'w-full min-w-0 truncate text-center font-bold'
      case 'labelicon':
        return 'ml-2 min-w-0 truncate font-bold'
      default:
        return 'min-w-0 truncate font-bold'
    }
  })
</script>

<style scoped>
  .shortcut-text {
    display: -webkit-box;
    min-width: 0;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }

  :deep(.p-contextmenu) {
    position: absolute !important;
    display: none !important;
  }
</style>

<i18n>
{
  "de": {
    "type": "Typ",
    "icon": "Symbol",
    "title": "Titel",
    "iconTitle": "Symbol und Titel",
    "full": "Vollständig",
    "widgets": {
      "shortcut-effort-capture": {
        "title": "Leistungserfassung",
        "text": "Erfassen Sie mühelos und präzise Ihre Leistungen. Verfolgen Sie Projekte, Arbeitsstunden und erreichte Meilensteine, um stets den Überblick über Ihre individuelle Performance zu behalten."
      },
      "shortcut-working-time-capture": {
        "title": "Stempeluhr",
        "text": "Verpassen Sie keine Minute und optimieren Sie Ihre Zeitverwaltung, während Sie einfach und effizient Arbeitszeiten protokollieren."
      },
      "shortcut-infoboard-news": {
        "title": "Neueste Beiträge",
        "text": "Bleiben Sie stets auf dem Laufenden mit dem Infoboard für die neuesten Einträge. Hier finden Sie aktuelle Informationen, Updates und relevante Ereignisse auf einen Blick - alles, was Sie wissen müssen, übersichtlich präsentiert."
      },
      "shortcut-time-tracking": {
        "title": "Time-Tracking",
        "text": "Ihre Stoppuhr im Netz, um präzise Zeiten für Ihre Leistungen zu erhalten. Erfassen Sie - mit oder ohne Verknüpfung zu einem Leistungsobjekt - die Zeit und überführen Sie diese mit Leichtigkeit direkt in eine erbrachte Leistung."
      },
      "shortcut-team-overview": {
        "title": "Mitarbeiterübersicht",
        "text": "Erhalten Sie einen Überblick über Ihre Kollegen in der Mitarbeiterübersicht. Hier finden Sie alle relevanten Informationen zu Krankheitsstatus und Erreichbarkeit. Behalten Sie den Zustand Ihres Teams im Blick und optimieren Sie die Zusammenarbeit."
      }
    }
  },
  "en": {
    "type": "Type",
    "icon": "Icon",
    "title": "Title",
    "iconTitle": "Icon and title",
    "full": "Full",
    "widgets": {
      "shortcut-effort-capture": {
        "title": "Effort capture",
        "text": "Capture your work accurately and effortlessly. Track projects, working hours and milestones to keep an overview of your individual performance."
      },
      "shortcut-working-time-capture": {
        "title": "Time clock",
        "text": "Make every minute count and optimize your time management by recording working hours simply and efficiently."
      },
      "shortcut-infoboard-news": {
        "title": "Latest posts",
        "text": "Stay up to date with the latest Infoboard posts. Find current information, updates and relevant events at a glance."
      },
      "shortcut-time-tracking": {
        "title": "Time tracking",
        "text": "Use the online stopwatch to record precise times for your work, with or without linking it to an effort object, and transfer it directly to completed work."
      },
      "shortcut-team-overview": {
        "title": "Employee overview",
        "text": "Get an overview of your colleagues, including availability and absence information, and keep track of your team."
      }
    }
  }
}
</i18n>
